import React from 'react';
import { Box, Card, CardActionArea, Typography, Checkbox } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

interface OptionCardProps {
  icon: SvgIconComponent;
  title: string;
  caption?: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  multiSelect?: boolean;
  /** 'medium' (default, 72px icon circle) or 'large' (100px — top-level category pickers). */
  size?: 'medium' | 'large';
}

const SIZES = {
  medium: { circle: 72, icon: 36 },
  large: { circle: 100, icon: 50 },
};

// Big, touch-friendly, icon-led selection card — used everywhere the wizard
// asks the user to pick from a set of options (product, camera hardware,
// inference model). Kept generic so every step shares the same look.
const OptionCard: React.FC<OptionCardProps> = ({ icon: Icon, title, caption, selected, onClick, disabled, multiSelect, size = 'medium' }) => {
  const { circle, icon } = SIZES[size];
  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        height: '100%',
        borderWidth: 2,
        borderColor: selected ? 'primary.main' : 'divider',
        backgroundColor: selected ? 'action.selected' : 'background.paper',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {multiSelect && (
        <Checkbox
          checked={selected}
          tabIndex={-1}
          sx={{ position: 'absolute', top: 4, right: 4 }}
        />
      )}
      <CardActionArea
        onClick={onClick}
        disabled={disabled}
        sx={{
          height: '100%',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: circle,
            height: circle,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: selected ? 'primary.main' : 'action.hover',
          }}
        >
          <Icon sx={{ fontSize: icon, color: selected ? 'primary.contrastText' : 'text.secondary' }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {title}
        </Typography>
        {caption && (
          <Typography variant="body2" color="text.secondary">
            {caption}
          </Typography>
        )}
      </CardActionArea>
    </Card>
  );
};

export default OptionCard;
